import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Pencil, Ruler, Undo2 } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import { Button } from "@/components/ui/Button";
import {
  getApplicationRegulacjaArea,
  resetApplicationRegulacjaGeometry,
  saveApplicationRegulacjaGeometry,
  setApplicationRegulacjaDeviceDescriptions,
  setApplicationRegulacjaDimensions,
} from "../../api";
import { fmtArea, fmtDateTime } from "../../utils";
import { objectIcon, objectSymbolSvg } from "../MapTab";
import { InfoHint } from "./InfoHint";

const HINT =
  "Pas liczy się sam z wytycznych TAURON (zarządzenie 48/2020, wersja trzecia), osobno " +
  "dla każdego rodzaju urządzenia: kabel ziemny jednotorowy to pas 0,5 m (0,25 m na " +
  "stronę od osi), słup i stacja słupowa to kwadrat 3×3 m liczony od środka, stacja " +
  "kontenerowa i złącze SN to obrys urządzenia powiększony o 0,5 m, a złącze nN i mufa " +
  "to sam obrys. Do pasa wchodzi WYŁĄCZNIE sieć projektowana, docięta do granic działki " +
  "z zapasem 1 cm. Stacji i złączy rysunek nie niesie z wymiarami — dopóki ich nie " +
  "wpiszesz, urządzenie NIE wchodzi do powierzchni (system nie zgaduje gabarytów, bo " +
  "zmyślony obrys ruszyłby metry, za które operator płaci właścicielowi). Włącz " +
  "„Edytuj obrys”, żeby poprawić pas ręcznie — od pierwszej zmiany obowiązuje TWÓJ " +
  "obrys. Ta sama geometria idzie na mapę zakresu w pakiecie i do skryptu " +
  "„regulacja.lsp” w paczce formatek.";

type PmLayer = L.Layer & {
  pm?: { enable: (o?: unknown) => void; disable: () => void };
};

// Ten sam słownik co przy terenie ULICP — geoman mówi wszędzie o „warstwie”, co przy
// rysowaniu obszaru na mapie nikomu nic nie mówi.
const TOOLBAR_PL = {
  tooltips: {
    firstVertex: "Kliknij, aby zacząć rysować obszar",
    continueLine: "Kliknij, aby dodać kolejny punkt",
    finishPoly: "Kliknij pierwszy punkt, aby zamknąć obszar",
    finishRect: "Kliknij, aby zamknąć prostokąt",
  },
  actions: {
    finish: "Zakończ",
    cancel: "Anuluj",
    removeLastVertex: "Cofnij ostatni punkt",
  },
  buttonTitles: {
    drawPolyButton: "Narysuj obszar",
    drawRectButton: "Narysuj prostokąt",
    editButton: "Przesuwaj wierzchołki",
    dragButton: "Przesuń cały obszar",
    cutButton: "Wytnij kawałek z obszaru",
    deleteButton: "Usuń obszar — włącz, potem kliknij obszar",
    removalTooltip: "Kliknij obszar, aby go usunąć",
  },
};

/** Pas służebności przesyłu (wniosek regulacyjny).
 *
 *  Mapa jest interaktywna z tego samego powodu co przy ULICP — tyle że tu stawka jest
 *  wyższa: pas ma pół metra szerokości, więc na statycznym obrazku jest kreską, a to
 *  z niego wychodzi powierzchnia, za którą operator płaci właścicielowi. */
export function RegulacjaAreaCard({ applicationId, disabled }: {
  applicationId: string;
  disabled?: boolean;
}) {
  const qc = useQueryClient();
  const queryKey = ["pp", "regulacja-area", applicationId];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getApplicationRegulacjaArea(applicationId),
  });
  const [edit, setEdit] = useState(false);
  const [dims, setDims] = useState<Record<string, { width_m: string; length_m: string }>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const contextRef = useRef<L.LayerGroup | null>(null);
  const stripRef = useRef<L.FeatureGroup | null>(null);
  const savingRef = useRef(false);
  const fittedRef = useRef(false);

  const onSaved = (fresh: unknown, { refreshApplication = true } = {}) => {
    qc.setQueryData(queryKey, fresh);
    if (refreshApplication) {
      qc.invalidateQueries({ queryKey: ["pp", "application", applicationId] });
    }
  };

  const saveGeometry = useMutation({
    mutationFn: (geometry: unknown) =>
      saveApplicationRegulacjaGeometry(applicationId, geometry),
    onSuccess: (fresh) => onSaved(fresh, { refreshApplication: false }),
  });
  const resetGeometry = useMutation({
    mutationFn: () => resetApplicationRegulacjaGeometry(applicationId),
    onSuccess: (fresh) => onSaved(fresh),
  });
  // Odrzucone wpisy trzymamy osobno: `data` jest nadpisywane każdym kolejnym GET-em,
  // a komunikat o złej wartości ma zostać na ekranie, dopóki człowiek go nie poprawi.
  const [rejected, setRejected] = useState<{ key: string; reason: string }[]>([]);
  const saveDimensions = useMutation({
    mutationFn: (payload: Record<string, { width_m: string; length_m: string }>) =>
      setApplicationRegulacjaDimensions(applicationId, payload),
    onSuccess: (fresh) => {
      onSaved(fresh);
      const bad = fresh.rejected_dimensions ?? [];
      setRejected(bad);
      // W polach zostawiamy TYLKO to, co odrzucono — poprawne wartości przychodzą już
      // z serwera, a odrzuconych człowiek nie może stracić z oczu, bo ma je poprawić.
      setDims((d) =>
        Object.fromEntries(
          Object.entries(d).filter(([kind]) => bad.some((r) => r.key === kind)),
        ),
      );
    },
  });

  // Opisy pól formularza OMI: lokalny szkic nadpisuje wartość z serwera dopiero po
  // pierwszym dotknięciu pola — inaczej każdy refetch kasowałby pisanie w trakcie.
  const [descDraft, setDescDraft] = useState<{ projektowane?: string; istniejace?: string }>({});
  const descValue = (key: "projektowane" | "istniejace") =>
    descDraft[key] ?? data?.device_descriptions?.[key] ?? "";
  const saveDescriptions = useMutation({
    mutationFn: (payload: { mode?: "ai" | "ai_geoportal"; projektowane?: string; istniejace?: string }) =>
      setApplicationRegulacjaDeviceDescriptions(applicationId, payload),
    onSuccess: (fresh) => {
      // Endpoint zwraca zapisane treści — wchodzą do cache'a payloadu i czyszczą szkic,
      // żeby pola pokazały dokładnie to, co poszło do bazy (i pójdzie do pisma).
      qc.setQueryData(queryKey, (old: unknown) =>
        old ? { ...(old as object), device_descriptions: fresh } : old,
      );
      setDescDraft({});
    },
  });

  // Jeden wiersz na RODZAJ urządzenia — także na taki, który wymiar już ma. Wpisany
  // wymiar obowiązuje wszystkie urządzenia tego rodzaju na trasie; wpis per obiekt
  // zostaje dostępny przez API, ale w formularzu byłby listą identycznych szafek.
  const devices = data?.device_summary ?? [];

  // Wartość w polu: to, co człowiek właśnie wpisał, a póki nie tknął — to, co zapisane.
  const fieldValue = (kind: string, field: "width_m" | "length_m") => {
    const typed = dims[kind]?.[field];
    if (typed !== undefined) return typed;
    const saved = devices.find((d) => d.kind === kind)?.[field];
    return saved == null ? "" : String(saved).replace(".", ",");
  };

  const applyDimensions = () => {
    // Wysyłamy KOMPLET: endpoint zapisuje słownik w całości, więc sam nowy wpis
    // skasowałby resztę. Wartości idą jako WPISANY TEKST — walidacja (przecinek, zero,
    // limit 100 m) siedzi na backendzie i to on umie powiedzieć, co jest nie tak.
    // Filtrowanie tutaj oznaczałoby, że zła wartość znika bez słowa.
    const payload: Record<string, { width_m: string; length_m: string }> = {};
    for (const device of devices) {
      const width = fieldValue(device.kind, "width_m");
      const length = fieldValue(device.kind, "length_m");
      // Oba pola puste = świadome wyczyszczenie wymiaru: klucz w ogóle nie idzie.
      if (!String(width).trim() && !String(length).trim()) continue;
      payload[device.kind] = { width_m: String(width), length_m: String(length) };
    }
    // Wpisy per obiekt (spoza formularza) zachowujemy bez ruszania.
    for (const [key, entry] of Object.entries(data?.dimensions ?? {})) {
      if (!devices.some((d) => d.kind === key)) {
        payload[key] = { width_m: String(entry.width_m), length_m: String(entry.length_m) };
      }
    }
    setRejected([]);
    saveDimensions.mutate(payload);
  };

  // Nazwa rodzaju urządzenia zamiast surowego klucza w komunikacie o odrzuceniu.
  const labelForKey = (key: string) =>
    devices.find((d) => d.kind === key)?.label ??
    data?.rules.find((r) => r.kind === key)?.label ??
    data?.missing_dimensions.find((m) => m.object_id === key)?.label ??
    key;

  // ── mapa ──
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [52.0, 19.0],
      zoom: 6,
      // Pas ma pół metra szerokości — żeby go obejrzeć, trzeba zejść GŁĘBIEJ niż sięgają
      // kafle OSM (z19). maxNativeZoom skaluje ostatni poziom kafli zamiast blokować zoom.
      maxZoom: 23,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 23,
      maxNativeZoom: 19,
    }).addTo(map);
    contextRef.current = L.layerGroup().addTo(map);
    stripRef.current = L.featureGroup().addTo(map);
    map.pm.setLang("pl", TOOLBAR_PL, "pl");
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  const dataRef = useRef(data);
  dataRef.current = data;
  const contextKey =
    `${data?.per_parcel.map((p) => p.parcel_id).join(",") ?? ""}` +
    `|${data?.route ? 1 : 0}|${(data?.devices ?? []).map((d) => d.slug).join(",")}`;
  const stripKey = edit ? "edit" : `${data?.computed_at ?? ""}|${data?.area_m2 ?? ""}`;

  // kontekst: działki wniosku + sieć projektowana
  useEffect(() => {
    const map = mapRef.current;
    const group = contextRef.current;
    const data = dataRef.current;
    if (!map || !group || !data) return;
    group.clearLayers();
    for (const row of data.per_parcel) {
      if (!row.geometry) continue;
      L.geoJSON(row.geometry as never, {
        style: { color: "#555", weight: 1, fillColor: "#93c5fd", fillOpacity: 0.2 },
        pmIgnore: true,
        interactive: false,
      })
        .bindTooltip(`dz. ${row.short_number || row.teryt} — ${fmtArea(row.area_m2)}`)
        .addTo(group);
    }
    if (data.route)
      L.geoJSON(data.route as never, {
        style: { color: "#dc1f1f", weight: 3 },
        pmIgnore: true,
        interactive: false,
      }).addTo(group);
    // Urządzenia symbolami jak na mapie projektu/przetargów (kwadrat = stacja,
    // romb = złącze, trójkąt = słup) — bez pointToLayer Leaflet rysowałby tu
    // domyślne niebieskie pinezki.
    for (const device of data.devices ?? []) {
      if (!device.geometry) continue;
      L.geoJSON(device.geometry as never, {
        pointToLayer: (_feature, latlng) =>
          L.marker(latlng, { icon: objectIcon(device.slug || device.kind) } as never),
        pmIgnore: true,
      })
        .bindTooltip(device.label)
        .addTo(group);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  // pas służebności + tryb edycji
  useEffect(() => {
    const map = mapRef.current;
    const group = stripRef.current;
    const data = dataRef.current;
    if (!map || !group || !data) return;
    group.clearLayers();
    if (data.geometry) {
      L.geoJSON(data.geometry as never, {
        style: {
          color: "#15803d",
          weight: 2,
          fillColor: "#16a34a",
          fillOpacity: edit ? 0.2 : 0.35,
        },
        pmIgnore: !edit,
        interactive: edit,
      }).eachLayer((layer) => group.addLayer(layer));
    }

    const raf = requestAnimationFrame(() => {
      map.invalidateSize();
      if (fittedRef.current) return;
      const bounds = group.getBounds();
      const target = bounds.isValid()
        ? bounds
        : data.route
          ? L.geoJSON(data.route as never).getBounds()
          : null;
      if (target?.isValid()) {
        // Pas ma pół metra — bez ciaśniejszego zoomu projektant zobaczy kreskę.
        map.fitBounds(target, { padding: [40, 40], maxZoom: 21 });
        fittedRef.current = true;
      }
    });

    if (!edit) {
      map.pm.removeControls();
      return () => cancelAnimationFrame(raf);
    }

    map.pm.addControls({
      position: "topright",
      drawMarker: false,
      drawCircle: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawRectangle: true,
      drawPolygon: true,
      drawText: false,
      editMode: true,
      dragMode: true,
      cutPolygon: true,
      removalMode: true,
      rotateMode: false,
    });
    map.pm.setGlobalOptions({ allowSelfIntersection: false, layerGroup: group });

    const persist = async () => {
      if (savingRef.current) return;
      savingRef.current = true;
      try {
        const polygons: GeoJSON.Position[][][] = [];
        group.eachLayer((layer) => {
          // Leaflet nie wyrzuca warstwy z FeatureGroup po usunięciu z mapy — bez tego
          // sprawdzenia skasowany obszar wracałby przy najbliższym zapisie.
          if (!map.hasLayer(layer)) return;
          // Precyzja 9 miejsc (~0,1 mm): domyślne 6 zaokrągla wierzchołki o ±11 cm,
          // co przy pasie szerokości 0,5 m wykrzywia obrys o 20–40% na każdym punkcie.
          const gj = (layer as L.Polygon).toGeoJSON?.(9) as GeoJSON.Feature | undefined;
          const geom = gj?.geometry;
          if (!geom) return;
          if (geom.type === "Polygon") polygons.push(geom.coordinates);
          else if (geom.type === "MultiPolygon") polygons.push(...geom.coordinates);
        });
        await saveGeometry.mutateAsync(
          polygons.length ? { type: "MultiPolygon", coordinates: polygons } : null,
        );
      } finally {
        savingRef.current = false;
      }
    };

    const bindLayer = (layer: L.Layer) => {
      (layer as PmLayer).pm?.enable({ allowSelfIntersection: false });
      layer.on("pm:edit", persist);
      layer.on("pm:markerdragend", persist);
      layer.on("pm:dragend", persist);
      layer.on("pm:remove", () => {
        group.removeLayer(layer);
        persist();
      });
    };

    const onCreate = (e: { layer: L.Layer }) => {
      if (!group.hasLayer(e.layer)) group.addLayer(e.layer);
      bindLayer(e.layer);
      persist();
    };
    map.on("pm:create", onCreate);
    map.on("pm:remove", (e: { layer: L.Layer }) => {
      group.removeLayer(e.layer);
      persist();
    });
    map.on("pm:cut", persist);
    group.eachLayer(bindLayer);

    return () => {
      cancelAnimationFrame(raf);
      map.off("pm:create");
      map.off("pm:remove");
      map.off("pm:cut");
      group.eachLayer((layer) => {
        (layer as PmLayer).pm?.disable();
        layer.off("pm:edit");
        layer.off("pm:markerdragend");
        layer.off("pm:dragend");
        layer.off("pm:remove");
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripKey]);

  const busy = saveGeometry.isPending || resetGeometry.isPending || saveDimensions.isPending;
  const failed = saveGeometry.isError || resetGeometry.isError || saveDimensions.isError;

  return (
    <div className="space-y-2 border border-[var(--color-border)] p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="section-label">Pas służebności przesyłu</div>
          <InfoHint text={HINT} />
        </div>
        <div className="flex items-center gap-2 text-xs">
          {!disabled && (
            <Button
              size="sm"
              variant={edit ? "primary" : "secondary"}
              disabled={busy || isLoading}
              onClick={() => {
                setEdit((v) => {
                  if (v) qc.invalidateQueries({ queryKey: ["pp", "application", applicationId] });
                  return !v;
                });
              }}
              title="Rysuj nowe poligony, przesuwaj wierzchołki, kasuj kawałki — zapis po każdej zmianie"
            >
              <Pencil size={13} className="mr-1" />
              {edit ? "Zakończ edycję" : "Edytuj obrys"}
            </Button>
          )}
          {!disabled && data?.is_customized && (
            <Button
              size="sm"
              variant="ghost"
              loading={resetGeometry.isPending}
              title="Porzuć ręczny obrys i policz pas od nowa z wytycznych TAURON"
              onClick={() => resetGeometry.mutate()}
            >
              <Undo2 size={13} className="mr-1" /> Przelicz z wytycznych
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-base font-semibold">{data ? fmtArea(data.area_m2) : "—"}</span>
        <span className="text-[11px] text-[var(--color-text-muted)]">
          powierzchnia pasa służebności ·{" "}
          {data?.is_customized
            ? "obrys rysowany ręcznie"
            : "szerokości wg wytycznych TAURON, docięte do granic działki"}
          {data?.computed_at && ` · przeliczono ${fmtDateTime(data.computed_at)}`}
        </span>
      </div>

      {/* Urządzenia bez obrysu — powód, dla którego powierzchnia bywa mniejsza, niż
          projektant się spodziewa. Formularz stoi NAD mapą, żeby był pierwszą rzeczą,
          którą widać po wejściu na kartę. */}
      {/* Także po złożeniu wniosku — wtedy tylko do odczytu. Bez tego nie da się już
          sprawdzić, jakie obrysy dały tę powierzchnię. */}
      {devices.length > 0 && (
        <div
          className={`space-y-1.5 border p-2 ${
            devices.some((d) => d.missing > 0)
              ? "border-amber-300 bg-amber-50"
              : "border-[var(--color-border)]"
          }`}
        >
          <div
            className={`flex items-center gap-1.5 text-xs font-medium ${
              devices.some((d) => d.missing > 0)
                ? "text-amber-800"
                : "text-[var(--color-text-secondary)]"
            }`}
          >
            <Ruler size={13} />
            {devices.some((d) => d.missing > 0)
              ? "Urządzenia bez wymiarów — nie wchodzą do pasa"
              : "Obrysy urządzeń"}
          </div>
          <div className="text-[11px] text-[var(--color-text-secondary)]">
            Rysunek niesie te urządzenia jako pojedyncze punkty, a wytyczne liczą ich pas
            od OBRYSU. Wpisany wymiar obowiązuje wszystkie urządzenia danego rodzaju na
            trasie; wyczyszczenie obu pól usuwa go z powrotem.
          </div>
          {!disabled && data?.is_customized && (
            <div className="text-[11px] font-medium text-amber-800">
              Pas jest teraz z obrysu rysowanego ręcznie — zapis wymiarów wróci do
              liczenia z wytycznych (ręczny obrys zostanie porzucony).
            </div>
          )}
          {devices.map((row) => (
            <div key={row.kind} className="flex items-center gap-2 text-xs flex-wrap">
              <span className="min-w-[15rem]">
                {row.label}{" "}
                <span className="text-[var(--color-text-muted)]">
                  ({row.count} szt.
                  {row.missing > 0 ? `, ${row.missing} bez wymiaru` : ""})
                </span>
              </span>
              <input
                type="text"
                inputMode="decimal"
                className="input-base w-20 py-0.5 text-xs"
                placeholder="szer. [m]"
                aria-label={`Szerokość: ${row.label}`}
                value={fieldValue(row.kind, "width_m")}
                disabled={busy || disabled}
                onChange={(e) =>
                  setDims((d) => ({
                    ...d,
                    [row.kind]: {
                      length_m: d[row.kind]?.length_m ?? fieldValue(row.kind, "length_m"),
                      width_m: e.target.value,
                    },
                  }))
                }
              />
              <span className="text-[var(--color-text-muted)]">×</span>
              <input
                type="text"
                inputMode="decimal"
                className="input-base w-20 py-0.5 text-xs"
                placeholder="dł. [m]"
                aria-label={`Długość: ${row.label}`}
                value={fieldValue(row.kind, "length_m")}
                disabled={busy || disabled}
                onChange={(e) =>
                  setDims((d) => ({
                    ...d,
                    [row.kind]: {
                      width_m: d[row.kind]?.width_m ?? fieldValue(row.kind, "width_m"),
                      length_m: e.target.value,
                    },
                  }))
                }
              />
            </div>
          ))}
          {!disabled && (
            <Button
              size="sm"
              variant="secondary"
              loading={saveDimensions.isPending}
              disabled={busy || Object.keys(dims).length === 0}
              onClick={applyDimensions}
            >
              Zapisz wymiary i przelicz pas
            </Button>
          )}
          {/* Odrzucone wpisy zostają w polach, żeby było co poprawiać — komunikat mówi,
              co jest nie tak, zamiast kasować wartość bez słowa. */}
          {rejected.map((row) => (
            <div key={row.key} className="text-[11px] text-red-700 flex items-start gap-1">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>
                <span className="font-medium">{labelForKey(row.key)}</span> — nie zapisano:{" "}
                {row.reason}
              </span>
            </div>
          ))}
        </div>
      )}

      {edit && (
        <div className="text-[11px] text-[var(--color-text-secondary)]">
          Narzędzia są w prawym górnym rogu mapy: rysowanie obszaru, przesuwanie
          wierzchołków, przesuwanie całego obszaru, wycinanie kawałka i kosz. Każda zmiana
          zapisuje się od razu — powierzchnia, mapa w pakiecie i skrypt CAD idą za nią.
        </div>
      )}

      <div className="relative isolate z-0">
        <div
          ref={containerRef}
          className="w-full border border-[var(--color-border)]"
          style={{ height: 420, background: "#fff" }}
        />
        <div className="absolute bottom-2 left-2 z-[1000] bg-[var(--color-bg-surface)] border border-[var(--color-border)] shadow-sm px-2 py-1 font-mono text-[10px] text-[var(--color-text-secondary)] space-y-0.5">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-0.5" style={{ background: "#dc1f1f" }} />
            sieć projektowana
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-2" style={{ background: "#16a34a", opacity: 0.5 }} />
            pas służebności
          </div>
          {/* Symbole urządzeń — te same, którymi rysuje je mapa (hash slugu → kolor/kształt). */}
          {[...new Map((data?.devices ?? []).map((d) => [d.slug, d])).values()].map((d) => (
            <div key={d.slug} className="flex items-center gap-1.5">
              <span
                className="inline-flex"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: objectSymbolSvg(d.slug || d.kind, 12) }}
              />
              {d.label}
            </div>
          ))}
        </div>
      </div>

      {/* Opisy urządzeń do pól formularza OMI — treść MUSI odpowiadać temu, czego żąda
          druk (rodzaj, mb, ilość, pow. zajęta, MPZP). AI układa z faktów mapy i wpisanych
          wymiarów; człowiek poprawia; pismo czyta przez #regulacja_urzadzenia_*#. */}
      {data && (
        <div className="space-y-1.5 border border-[var(--color-border)] p-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">
              Opisy urządzeń do wniosku (pola formularza OMI)
            </span>
            {!disabled && (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  loading={saveDescriptions.isPending &&
                    saveDescriptions.variables?.mode === "ai"}
                  disabled={busy}
                  title="AI układa treść z faktów mapy: rodzaje, metry bieżące, sztuki, powierzchnie zajęte z wpisanych wymiarów, przeznaczenie MPZP. Niczego nie zmyśla — czego nie ma w danych, tego nie napisze."
                  onClick={() => saveDescriptions.mutate({ mode: "ai" })}
                >
                  Ułóż opisy AI
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={saveDescriptions.isPending &&
                    saveDescriptions.variables?.mode !== "ai"}
                  disabled={busy || Object.keys(descDraft).length === 0}
                  onClick={() =>
                    saveDescriptions.mutate({
                      projektowane: descValue("projektowane"),
                      istniejace: descValue("istniejace"),
                    })
                  }
                >
                  Zapisz opisy
                </Button>
              </div>
            )}
          </div>
          <label className="block text-[11px] text-[var(--color-text-muted)]">
            Projektowane na nieruchomości urządzenia elektroenergetyczne
            <textarea
              className="input-base w-full mt-0.5 text-xs"
              rows={3}
              value={descValue("projektowane")}
              disabled={busy || disabled}
              placeholder="rodzaj urządzeń, długość w mb, ilość, pow. zajęta w m² (stacje/słupy/złącza)…"
              onChange={(e) =>
                setDescDraft((d) => ({ ...d, projektowane: e.target.value }))
              }
            />
          </label>
          <label className="block text-[11px] text-[var(--color-text-muted)]">
            Istniejące na nieruchomości urządzenia elektroenergetyczne
            <textarea
              className="input-base w-full mt-0.5 text-xs"
              rows={2}
              value={descValue("istniejace")}
              disabled={busy || disabled}
              placeholder="jak wyżej — dla urządzeń już istniejących (puste = brak urządzeń)"
              onChange={(e) =>
                setDescDraft((d) => ({ ...d, istniejace: e.target.value }))
              }
            />
          </label>
        </div>
      )}

      {data && data.per_parcel.length > 0 && (
        <div className="space-y-0.5 text-[11px]">
          {data.per_parcel.map((row) => (
            <div key={row.parcel_id} className="flex items-center gap-2">
              <span className="font-mono">{row.short_number || row.teryt}</span>
              <span className="text-[var(--color-text-muted)]">{row.obreb || "—"}</span>
              <span className="ml-auto">{fmtArea(row.area_m2)}</span>
              <span className="text-[var(--color-text-muted)] w-24 text-right">
                z {fmtArea(row.parcel_area_m2)}
              </span>
            </div>
          ))}
        </div>
      )}

      {data?.warnings?.map((warning) => (
        <div key={warning} className="text-xs text-amber-600 flex items-start gap-1">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {warning}
        </div>
      ))}
      {failed && (
        <div className="text-xs text-red-600">
          Nie udało się zapisać pasa służebności — spróbuj ponownie.
        </div>
      )}
    </div>
  );
}
